import React from "react";
import { Form, Input, InputNumber, Select, FormInstance } from "antd";
import {
  SettingOutlined,
  RobotOutlined,
  ToolOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { Button } from "@/components/common/button";
import { InferenceOptionsOverrideEditor } from "@/components/ai-connection/InferenceOptionsOverrideEditor";
import { useNavigate } from "react-router-dom";
import { AgentFormValues } from "./agentFormValues";

interface AgentFormProps {
  form: FormInstance;
  onFinish: (values: AgentFormValues) => void;
  initialValues?: Partial<AgentFormValues>;
  isSubmitting?: boolean;
}

const TOOL_OPTIONS = [
  { label: "Code Interpreter", value: "code_interpreter" },
  { label: "Image Generation", value: "image_generation" },
  { label: "Web Search", value: "web_search" },
  { label: "Web Search Premium", value: "web_search_premium" },
  { label: "Document Library", value: "document_library" },
  { label: "Function: Roll Dice", value: "function_roll_dice" },
  {
    label: "Function: Vehicle Registration",
    value: "function_vehicle_registration",
  },
  { label: "Function: Address Lookup", value: "function_address_lookup" },
  {
    label: "Function: Phone Number Lookup",
    value: "function_phone_number_lookup",
  },
  {
    label: "Function: Create Repair Direct Dossier",
    value: "function_create_repair_direct_dossier",
  },
  {
    label: "Function: Lease Company Selector",
    value: "function_lease_company_selector",
  },
  {
    label: "Function: Service Partner Selector",
    value: "function_service_partner_selector",
  },
  {
    label: "Function: Update Damage Number Dossier",
    value: "function_update_damage_number_dossier",
  },
];

/**
 * Adapts {@see InferenceOptionsOverrideEditor}'s profile selector to Ant Design's Form field
 * value/onChange contract so the surrounding Form.Item can validate `aiProfile` (required).
 */
const AiProfileField: React.FC<{
  value?: string | null;
  onChange?: (value: string | null) => void;
}> = ({ value, onChange }) => (
  <InferenceOptionsOverrideEditor
    label="AI Profile"
    hideOverrides
    profileId={value}
    onChangeOverride={() => {}}
    onChangeProfile={(profileId) => onChange?.(profileId)}
  />
);

export const AgentForm: React.FC<AgentFormProps> = ({
  form,
  onFinish,
  initialValues,
  isSubmitting,
}) => {
  const navigate = useNavigate();

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{
        allowedTools: [],
        ...initialValues,
      }}
      className="space-y-6"
      requiredMark={false}
    >
      {/* Basic Configuration Section */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
        <div className="bg-slate-50/80 border-b border-gray-100 px-6 py-4 flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 shadow-sm">
            <SettingOutlined className="text-lg" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 m-0 leading-tight">
              Basic Configuration
            </h3>
            <p className="text-xs text-gray-500 m-0 mt-0.5">
              Identifiers and display names for the agent.
            </p>
          </div>
        </div>
        <div className="p-6 grid grid-cols-1 gap-x-8 gap-y-4">
          <Form.Item
            name="name"
            label={<span className="font-medium text-slate-700">Name</span>}
            rules={[
              {
                required: true,
                message: "Please enter the agent's internal name",
              },
              { max: 255, message: "Name must be at most 255 characters" },
            ]}
          >
            <Input
              className="rounded-lg font-mono text-sm hover:border-blue-400 focus:border-blue-500"
              size="large"
              placeholder="E.g., agent_claim_assistant"
            />
          </Form.Item>
        </div>
      </div>

      {/* LLM Configuration Section */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
        <div className="bg-slate-50/80 border-b border-gray-100 px-6 py-4 flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 shadow-sm">
            <RobotOutlined className="text-lg" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 m-0 leading-tight">
              LLM Configuration
            </h3>
            <p className="text-xs text-gray-500 m-0 mt-0.5">
              Define the core intelligence, instructions, and generation
              parameters.
            </p>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <Form.Item
            name="aiProfile"
            rules={[{ required: true, message: "Please select an AI Profile" }]}
            className="mb-0"
          >
            <AiProfileField />
          </Form.Item>

          <Form.Item
            name="instructions"
            label={
              <span className="font-medium text-slate-700">
                System Instructions
              </span>
            }
            rules={[
              {
                required: true,
                message: "Please provide the system instructions",
              },
            ]}
            className="mb-0"
          >
            <Input.TextArea
              className="rounded-xl hover:border-indigo-400 focus:border-indigo-500 text-sm leading-relaxed p-4"
              rows={8}
              placeholder="You are a highly capable AI assistant..."
            />
          </Form.Item>

          <div className="pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
            <Form.Item
              name="overrideTemperature"
              label={
                <span className="font-medium text-slate-700">
                  Temperature (override)
                </span>
              }
              tooltip="Per-request override of the AI profile's temperature. Leave blank to inherit."
              rules={[
                {
                  type: "number",
                  min: 0,
                  max: 2,
                  message: "Must be between 0 and 2",
                },
              ]}
            >
              <InputNumber
                className="w-full rounded-lg"
                size="large"
                step={0.1}
                placeholder="Inherit"
              />
            </Form.Item>

            <Form.Item
              name="overrideMaxTokens"
              label={
                <span className="font-medium text-slate-700">
                  Max Tokens (override)
                </span>
              }
              tooltip="Per-request output cap (provider max_tokens). Leave blank to inherit from the profile."
              rules={[
                {
                  type: "number",
                  min: 1,
                  message: "Must be a positive integer",
                },
              ]}
            >
              <InputNumber
                className="w-full rounded-lg"
                size="large"
                placeholder="Inherit"
              />
            </Form.Item>

            <Form.Item
              name="overrideTopP"
              label={
                <span className="font-medium text-slate-700">
                  Top P (override)
                </span>
              }
              tooltip="Per-request override of the AI profile's top_p. Leave blank to inherit."
              rules={[
                {
                  type: "number",
                  min: 0,
                  max: 1,
                  message: "Must be between 0 and 1",
                },
              ]}
            >
              <InputNumber
                className="w-full rounded-lg"
                size="large"
                step={0.05}
                placeholder="Inherit"
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <Form.Item
              name="maxTokensPerSession"
              label={
                <span className="font-medium text-slate-700">
                  Session Token Budget
                </span>
              }
              tooltip="Cumulative token budget for the whole session (across every turn). Once the accumulated usage reaches this budget, the next turn is refused with an HTTP 429 error and the session stops accepting new turns. The budget is only checked before a turn, so the first turn is always allowed. As a safety net, when no per-request Max Tokens (override) is set, this budget also caps the output of one-shot invocations and the first turn of a fresh conversation; every continued turn then relies solely on the cumulative guard."
              rules={[
                {
                  type: "number",
                  min: 1,
                  message: "Must be a positive integer",
                },
              ]}
            >
              <InputNumber
                className="w-full rounded-lg"
                size="large"
                placeholder="Unlimited"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="responseFormatSchema"
            label={
              <span className="font-medium text-slate-700">
                Response Format Schema (JSON)
              </span>
            }
            tooltip="Optional JSON Schema for structured output. Leave blank for free-form responses."
            className="mb-0"
            rules={[
              {
                validator: (_rule, value?: string) => {
                  if (!value || value.trim() === "") {
                    return Promise.resolve();
                  }
                  try {
                    JSON.parse(value);
                    return Promise.resolve();
                  } catch {
                    return Promise.reject(
                      new Error("Must be valid JSON or left empty"),
                    );
                  }
                },
              },
            ]}
          >
            <Input.TextArea
              className="rounded-xl hover:border-indigo-400 focus:border-indigo-500 font-mono text-sm leading-relaxed p-4"
              rows={8}
              placeholder='{"type": "object", "properties": {}}'
            />
          </Form.Item>
        </div>
      </div>

      {/* Capabilities Section */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
        <div className="bg-slate-50/80 border-b border-gray-100 px-6 py-4 flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center text-purple-600 shadow-sm">
            <ToolOutlined className="text-lg" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 m-0 leading-tight">
              Capabilities
            </h3>
            <p className="text-xs text-gray-500 m-0 mt-0.5">
              Equip the agent with specific tools and data access.
            </p>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <Form.Item
            name="allowedTools"
            label={
              <span className="font-medium text-slate-700">Allowed Tools</span>
            }
            className="mb-0"
          >
            <Select
              mode="multiple"
              options={TOOL_OPTIONS}
              size="large"
              className="rounded-lg"
              placeholder="Select allowed tools for this agent"
              allowClear
            />
          </Form.Item>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-8 pt-4 pb-8">
        <Button
          variant="outline"
          type="button"
          onClick={() => navigate("/agents")}
          disabled={isSubmitting}
          className="h-11 px-8 rounded-xl text-base font-medium flex items-center"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          disabled={isSubmitting}
          className="h-11 px-8 shadow-md shadow-blue-500/20 text-base font-medium rounded-xl transition-transform active:scale-95 flex items-center gap-2"
        >
          {isSubmitting ? (
            "Saving..."
          ) : (
            <>
              <SaveOutlined /> Save Agent
            </>
          )}
        </Button>
      </div>
    </Form>
  );
};
